<script setup lang="ts">
/**
 * Import from a connector. A connector fills a dataset through a source: the picked object or
 * table is bound to a dataset and synced into it as members. `mode` says which dataset — a new
 * typed container per pick, or the one already open, whose type is settled and not asked again.
 * Opened without a connector it asks which one first. For an HTTP API the endpoint itself is the
 * source and there is nothing to pick. During the work the same rows report each server outcome.
 */
import type { components } from '@scalty/contracts'
import Dialog from '~/components/ui/Dialog.vue'
import Field from '~/components/ui/Field.vue'
import Choice from '~/components/ui/Choice.vue'
import ChoiceGrid from '~/components/ui/ChoiceGrid.vue'
import PickList from '~/components/ui/PickList.vue'
import { DATASET_KINDS, acceptedWords, matchCount, suggestKind } from '~/utils/datasetKinds'
import { useDatasetKindCapabilities } from '~/composables/useDatasetKindCapabilities'
import { plural } from '~/utils/words'
import SearchBox from '~/components/ui/SearchBox.vue'
import Icon from '~/components/ui/Icon.vue'
import ActionButton from '~/components/ui/ActionButton.vue'
import StatusCell from '~/components/ui/StatusCell.vue'
import Skeleton from '~/components/ui/Skeleton.vue'

type S = components['schemas']
/* Not `Pick`: a local type of that name shadows the built-in utility type inside the
   generated template code, and the generic list below then loses its item type. */
export type SourcePick = S['ObjectPick'] | S['TablePick']
export interface PickItem { id: string; title: string; facts: string; pick: SourcePick }
export interface PickProgress { phase: 'creating' | 'ready' | 'refused'; problem?: string }
const props = withDefaults(defineProps<{
  /** Null until one is chosen, which is how the dataset page opens this dialog. */
  connector: { id: string; name: string; type: S['ConnectorType'] } | null
  /** The project's connectors, offered when none is named yet. */
  connectors?: { id: string; name: string; facts: string }[]
  /** `dataset` creates a typed container per pick; `source` attaches to the dataset already open. */
  mode?: 'dataset' | 'source'
  entity?: string
  items: PickItem[]
  truncated: boolean
  pending: boolean
  unreachable: string | null
  prefix: string
  progress?: Record<string, PickProgress>
}>(), { progress: undefined, connectors: () => [], mode: 'dataset', entity: '' })
const emit = defineEmits<{ close: []; submit: [payload: { name: string; kind: string; picks: SourcePick[] }]; choose: [connectorId: string]; 'update:prefix': [value: string] }>()
const picked = ref<string[]>([])
const name = ref('')
/* A new container needs a type; attaching to one that exists never asks. */
// The accept list belongs to the server; this dialog only shows it (W8-WALK).
useDatasetKindCapabilities()
const kind = ref(DATASET_KINDS.find((entry) => entry.available)?.id ?? '')
const api = computed(() => props.connector?.type === 'api')
const unit = computed(() => props.connector?.type === 's3' ? 'object' : 'table')
const asks = computed(() => props.mode === 'dataset')
const creating = computed(() => props.progress !== undefined)
const toggle = (id: string) => {
  if (creating.value) return
  picked.value = picked.value.includes(id) ? picked.value.filter((entry) => entry !== id) : [...picked.value, id]
}
const all = computed(() => props.items.length > 0 && props.items.every((entry) => picked.value.includes(entry.id)))
const toggleAll = () => { picked.value = all.value ? [] : props.items.map((entry) => entry.id) }
const chosen = computed(() => props.items.filter((entry) => picked.value.includes(entry.id)))
const complete = computed(() => {
  if (!props.connector) return false
  if (asks.value && !kind.value) return false
  return api.value ? (!asks.value || Boolean(name.value.trim())) : chosen.value.length > 0
})
const verb = computed(() => props.mode === 'source'
  ? (chosen.value.length > 1 ? `Attach ${chosen.value.length} sources` : 'Attach source')
  : (chosen.value.length > 1 ? `Create ${chosen.value.length} datasets` : 'Create dataset'))
const TYPE: Record<S['ConnectorType'], string> = { s3: 'S3-compatible store', postgres: 'Postgres', mysql: 'MySQL', api: 'HTTP API' }
const shown = computed(() => creating.value ? props.items.filter((entry) => entry.id in (props.progress ?? {})) : props.items)
const meta = computed(() => {
  if (!props.connector) return `${plural(props.connectors.length, 'connector')} in this project`
  if (creating.value) return props.mode === 'source' ? `attaching ${plural(shown.value.length, 'source')}` : `creating ${plural(shown.value.length, 'dataset')}`
  if (api.value) return `${TYPE.api} · the endpoint is the source`
  return `${TYPE[props.connector.type]} · ${props.mode === 'source' ? `one source per ${unit.value}` : `one dataset per ${unit.value}`}`
})
const countLine = computed(() => {
  if (creating.value) {
    const results = Object.values(props.progress ?? {})
    const refused = results.filter((result) => result.phase === 'refused').length
    const done = props.mode === 'source' ? 'attached' : 'created'
    return `${results.filter((result) => result.phase === 'ready').length} of ${shown.value.length} ${done}${refused ? ` · ${refused} refused` : ''}`
  }
  return chosen.value.length ? `${chosen.value.length} selected of ${props.items.length}` : `${props.items.length} ${unit.value}s`
})
const WORD = { creating: 'Creating', ready: 'Ready', refused: 'Refused' }
const creationPending = computed(() => Object.values(props.progress ?? {}).some((result) => result.phase === 'creating'))
/* The picked names point at a type the same way loaded files do in the creation dialog. */
const pickNames = computed(() => props.items.filter((entry) => picked.value.includes(entry.id)).map((entry) => entry.title))
const suggested = computed(() => { const guess = suggestKind(pickNames.value); return guess && guess !== kind.value ? guess : null })
const suggestedLabel = computed(() => DATASET_KINDS.find((entry) => entry.id === suggested.value)?.label ?? '')
const types = computed(() => DATASET_KINDS.map((entry) => ({
  id: entry.id, title: entry.label, icon: entry.icon, disabled: !entry.available,
  facts: !entry.available ? 'not yet available' : pickNames.value.length ? `${matchCount(entry, pickNames.value)} of ${pickNames.value.length}` : acceptedWords(entry),
})))
const submit = () => emit('submit', { name: name.value.trim(), kind: kind.value, picks: chosen.value.map((entry) => entry.pick) })
</script>

<template>
  <Dialog :title="!connector ? (mode === 'source' ? 'Attach source' : 'New dataset from a connector') : api ? 'New dataset from source' : 'Import from ' + connector.name" :meta="meta" :width="600" @close="emit('close')">
    <template #icon><Icon name="datasets" /></template>

    <!-- No connector named yet: which one reads for this dataset. -->
    <template v-if="!connector">
      <p v-if="!connectors.length" class="m-0 text-xs leading-relaxed text-muted-foreground">This project has no connector yet. Create one under the organisation's connectors, then attach it here.</p>
      <PickList v-else v-slot="{ shown: choices }" :items="connectors" unit="connectors" height="var(--measure-240px)">
        <Choice v-for="option in choices" :key="option.id" :title="option.name" :facts="option.facts" :selected="false" @click="emit('choose', option.id)" />
      </PickList>
    </template>

    <template v-else-if="api">
      <p class="m-0 text-xs leading-relaxed text-muted-foreground">{{ mode === 'source' ? 'The endpoint in this connector\u2019s settings is read in full and its content joins this dataset.' : 'The endpoint in this connector\u2019s settings is read in full and stored as one dataset.' }}</p>
      <Field v-if="asks" v-model="name" label="Dataset name" required />
    </template>

    <template v-else>
      <div v-if="asks && !creating" class="flex shrink-0 flex-col gap-1.5">
        <span class="flex items-center justify-between text-xs text-muted-foreground">
          Type
          <span v-if="suggestedLabel" class="num text-[length:var(--type-fine)]">the picks point at {{ suggestedLabel }}</span>
        </span>
        <ChoiceGrid v-model="kind" :options="types" :columns="3" height="var(--measure-160px)" />
        <span class="num text-[length:var(--type-fine)] text-muted-foreground">Every dataset made here gets this type, and it cannot be changed afterwards.</span>
      </div>

      <div v-if="!creating" class="flex items-center gap-2">
        <SearchBox v-if="connector.type === 's3'" :model-value="prefix" placeholder="Narrow by prefix…" class="min-w-0 flex-1" @update:model-value="emit('update:prefix', $event)" />
        <span v-else class="flex-1" />
        <span class="num text-[length:var(--type-fine)] text-muted-foreground">{{ countLine }}</span>
      </div>

      <div class="overflow-hidden rounded-md border border-border">
        <div
class="grid h-7 items-center gap-x-3 border-b border-border bg-secondary/50 px-3 text-[length:var(--type-meta)] font-semibold text-muted-foreground"
             :style="{ gridTemplateColumns: creating ? 'minmax(0,1fr) var(--table-track-200)' : 'var(--measure-14px) minmax(0,1fr) var(--table-track-160)' }">
          <button v-if="!creating" type="button" class="hit grid size-3.5 place-items-center rounded-[3px] border" :class="all ? 'border-primary bg-primary text-primary-foreground' : 'border-border'" :aria-label="all ? 'Clear selection' : 'Select all'" @click="toggleAll"><Icon v-if="all" name="check" :size="10" /></button>
          <span>{{ unit === 'object' ? 'Object' : 'Table' }}</span>
          <span>{{ creating ? 'Status' : (unit === 'object' ? 'Size · modified' : 'Rows') }}</span>
        </div>

        <div class="max-h-[var(--measure-400px)] overflow-auto">
          <template v-if="pending && !creating">
            <div v-for="n in 6" :key="n" class="grid h-[var(--row-height)] items-center gap-x-3 border-b border-border/50 px-3 last:border-b-0" style="grid-template-columns: var(--measure-14px) minmax(0,1fr) var(--table-track-160)">
              <span /><Skeleton width="66%" /><Skeleton width="50%" />
            </div>
          </template>
          <p v-else-if="unreachable" role="alert" class="m-0 px-3 py-4 text-xs text-destructive">{{ unreachable }}</p>
          <p v-else-if="!shown.length" class="m-0 px-3 py-4 text-xs text-muted-foreground">{{ connector.type === 's3' ? 'Nothing under this prefix.' : 'No tables visible to the stored credentials.' }}</p>
          <button
v-for="entry in shown" v-else :key="entry.id" type="button"
                  class="grid w-full cursor-pointer items-center gap-x-3 border-b border-border/50 px-3 text-left last:border-b-0 hover:bg-sidebar-accent/60"
                  :class="[creating ? 'min-h-[var(--row-height-detail)] py-1.5' : 'h-[var(--row-height)]', !creating && picked.includes(entry.id) && 'bg-sidebar-accent']"
                  :style="{ gridTemplateColumns: creating ? 'minmax(0,1fr) var(--table-track-200)' : 'var(--measure-14px) minmax(0,1fr) var(--table-track-160)' }"
                  @click="toggle(entry.id)">
            <span v-if="!creating" class="grid size-3.5 place-items-center rounded-[3px] border" :class="picked.includes(entry.id) ? 'border-primary bg-primary text-primary-foreground' : 'border-border'"><Icon v-if="picked.includes(entry.id)" name="check" :size="10" /></span>
            <span class="flex min-w-0 items-center gap-2 text-xs">
              <Icon :name="unit === 'object' ? 'file' : 'table'" :size="12" class="shrink-0 text-muted-foreground" />
              <span class="truncate">{{ entry.title }}</span>
            </span>
            <span v-if="creating" class="flex min-w-0 flex-col gap-0.5 leading-[1.25]">
              <StatusCell :status="WORD[progress?.[entry.id]?.phase ?? 'creating']" />
              <span v-if="progress?.[entry.id]?.problem" class="num whitespace-normal break-words text-[length:var(--type-fine)] text-destructive">{{ progress?.[entry.id]?.problem }}</span>
            </span>
            <span v-else class="num truncate text-[length:var(--type-meta)] text-muted-foreground">{{ entry.facts }}</span>
          </button>
        </div>
      </div>

      <p class="num m-0 text-[length:var(--type-fine)] text-muted-foreground">
        <template v-if="creating">{{ countLine }}</template>
        <template v-else-if="truncated">The list is cut at the server's limit{{ connector.type === 's3' ? ' · narrow the prefix to see the rest' : '' }}</template>
        <template v-else-if="mode === 'source'">Each pick becomes a source of {{ entity || 'this dataset' }} and syncs into it</template>
        <template v-else>Each pick becomes its own dataset, named after the {{ unit }}</template>
      </p>
    </template>

    <template #actions>
      <ActionButton :label="creating ? 'Close' : 'Cancel'" :disabled="creating && creationPending" @click="emit('close')" />
      <ActionButton v-if="!creating" :label="verb" variant="primary" :disabled="!complete" @click="submit"><template #icon><Icon name="download" /></template></ActionButton>
    </template>
  </Dialog>
</template>
